import { useCallback, useMemo, useState } from 'react';
import { PanelDefinition, QueryDefinition } from '../../../core';

/**
 * UsePanelEditor is used in PanelEditorForm
 * This hook stores the states of panel definition and returns the onChange handlers for each state
 */
export const usePanelEditor = (initialPanelDefinition: PanelDefinition) => {
  const {
    display,
    plugin,
    queries: initialQueries,
    query_options: initialQueryOptions,
    links: initialLinks,
    formulas: initialFormulas,
  } = initialPanelDefinition.spec;
  const [name, setName] = useState(display.name);
  const [description, setDescription] = useState(display.description);
  const [links, setLinks] = useState(initialLinks ?? []);
  const [formulas, setFormulas] = useState(initialFormulas ?? []);
  const [queryOptions, setQueryOptions] = useState(initialQueryOptions);

  // need to keep track of prevQueries if switching from a panel with no queries (ex: markdown) to one with queries
  const [prevQueries, setPrevQueries] = useState(initialQueries);

  // must update panel spec and queries simultaneously to avoid incompatibility.
  // for example, if panel kind is BarChart, we need to update the prom query spec to include the aggregator field.
  const [panelSpecAndQueries, setPanelSpecAndQueries] = useState({ spec: plugin, queries: initialQueries });

  const setPlugin = useCallback(
    (plugin: PanelDefinition['spec']['plugin']) => {
      setPanelSpecAndQueries({ ...panelSpecAndQueries, spec: plugin });
    },
    [panelSpecAndQueries]
  );

  /**
   * If hideQueryEditor is true, set panelDefinition.spec.queries to undefined.
   * If hideQueryEditor is false and query is undefined, set panelDefinition.spec.queries to previous queries.
   */
  const setQueries = useCallback(
    (queries?: QueryDefinition[], hideQueryEditor?: boolean) => {
      if (hideQueryEditor) {
        setPrevQueries(panelSpecAndQueries.queries);
        setPanelSpecAndQueries({ ...panelSpecAndQueries, queries: undefined });
      } else {
        setPanelSpecAndQueries({ ...panelSpecAndQueries, queries: queries === undefined ? prevQueries : queries });
      }
    },
    [panelSpecAndQueries, prevQueries]
  );

  // reset panel definition
  const setPanelDefinition = useCallback(
    (panelDefinition: PanelDefinition) => {
      const { display, plugin, queries, query_options, links, formulas } = panelDefinition.spec;
      setName(display.name);
      setDescription(display.description);
      setPanelSpecAndQueries({ spec: plugin, queries });
      setLinks(links ?? []);
      setFormulas(formulas ?? []);
      setQueryOptions(query_options);
    },
    [setName, setDescription, setLinks, setQueryOptions]
  );

  return useMemo(
    () => ({
      panelDefinition: {
        kind: 'Panel',
        spec: {
          display: { name, description },
          plugin: panelSpecAndQueries.spec,
          queries: panelSpecAndQueries.queries,
          query_options: queryOptions,
          formulas,
          links,
        },
      } as PanelDefinition,
      setName,
      description,
      setDescription,
      setPlugin,
      setQueries,
      setPanelDefinition,
      setLinks,
      setFormulas,
      setQueryOptions,
    }),
    [
      name,
      description,
      panelSpecAndQueries.spec,
      panelSpecAndQueries.queries,
      links,
      formulas,
      queryOptions,
      setPlugin,
      setQueries,
      setPanelDefinition,
      setFormulas,
      setQueryOptions,
    ]
  );
};
